/**
 * Auto-track page_view events on route changes for SEO pages.
 * Mounted once at the app level; uses location.pathname as the key.
 */
import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { trackEvent } from "./useTrackEvent";

const STATIC_DENYLIST = new Set<string>([
  "auth",
  "my-account",
  "admin",
  "admin/seo-roi",
  "cart",
  "checkout",
]);

/**
 * Returns true for pages we want to count in the SEO dashboard.
 * Skips admin / auth / account routes.
 */
const isTrackable = (path: string): boolean => {
  const slug = path.replace(/^\/+/, "").split("?")[0];
  if (!slug) return true; // homepage
  if (STATIC_DENYLIST.has(slug)) return false;
  if (slug.startsWith("admin/")) return false;
  if (slug.startsWith("auth")) return false;
  return true;
};

/**
 * Cross-mount dedup window. Prevents the same slug from being counted twice
 * within 2 seconds (e.g. StrictMode double-mount, navigation race, hash change).
 */
const DEDUP_KEY = "nardy_pv_last";
const DEDUP_WINDOW_MS = 2000;

const wasJustTracked = (slug: string): boolean => {
  try {
    const raw = window.sessionStorage.getItem(DEDUP_KEY);
    if (!raw) return false;
    const [lastSlug, lastTs] = raw.split("|");
    if (lastSlug !== slug) return false;
    return Date.now() - Number(lastTs) < DEDUP_WINDOW_MS;
  } catch {
    return false;
  }
};

const markTracked = (slug: string) => {
  try {
    window.sessionStorage.setItem(DEDUP_KEY, `${slug}|${Date.now()}`);
  } catch {
    /* ignore */
  }
};

export const usePageViewTracker = () => {
  const location = useLocation();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    const path = location.pathname;
    if (lastPath.current === path) return;
    lastPath.current = path;
    if (!isTrackable(path)) return;

    const slug = path.replace(/^\/+/, "") || "home";
    if (wasJustTracked(slug)) return;

    // Defer one tick so route component <title> updates before we capture it.
    const handle = window.setTimeout(() => {
      if (wasJustTracked(slug)) return; // re-check after delay
      markTracked(slug);
      trackEvent("page_view", {
        page_slug: slug,
        title: document.title.slice(0, 200),
      });
    }, 50);

    return () => window.clearTimeout(handle);
  }, [location.pathname]);
};
